import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  updateProfile,
  updatePassword,
  sendPasswordResetEmail,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { isNative } from '../utils/platform';

// Google Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

const ALLOWED_SIGNUP_ROLES = ['owner', 'caregiver'];

const normalizeAuthUser = (uid, email, data = {}) => ({
  id: uid,
  uid,
  email: email || data.email || '',
  ...data,
});

/**
 * Sign up with email and password
 */
export const signUpWithEmail = async (email, password, userData) => {
  try {
    const role = ALLOWED_SIGNUP_ROLES.includes(userData.role) ? userData.role : 'owner';

    // Create Firebase auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Create user profile in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: user.email,
      name: userData.name,
      displayName: userData.displayName || userData.name,
      role, // 'owner' or 'caregiver' only via public signup
      phone: userData.phone || '',
      avatar: userData.avatar || '',
      location: userData.location || '',
      isAvailable: userData.role === 'caregiver' ? false : null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Role-specific fields
      ...(role === 'caregiver' && {
        services: [],
        experience: '',
        bio: '',
        rating: 0,
        totalReviews: 0
      }),
      ...(role === 'owner' && {
        pets: []
      })
    });

    // Update Firebase Auth profile
    await updateProfile(user, {
      displayName: userData.displayName || userData.name
    });

    return {
      success: true,
      user: normalizeAuthUser(user.uid, user.email, {
        ...userData,
        role,
      })
    };
  } catch (error) {
    console.error('Sign up error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (emailOrUsername, password) => {
  try {
    let email = emailOrUsername;
    
    // Check if input is username (not email format)
    if (!emailOrUsername.includes('@')) {
      // It's a username - need to find the email
      const { getDocs, collection, query, where } = await import('firebase/firestore');
      const usersQuery = query(collection(db, 'users'), where('username', '==', emailOrUsername));
      const usersSnapshot = await getDocs(usersQuery);
      
      if (usersSnapshot.empty) {
        throw new Error('User not found. Please check your username/email.');
      }
      
      // Get email from username
      email = usersSnapshot.docs[0].data().email;
    }
    
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Get user profile from Firestore (use cache first for speed)
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    if (!userDoc.exists()) {
      return {
        success: false,
        error: 'User profile not found. Please contact support.'
      };
    }

    const userData = userDoc.data();
    
    return {
      success: true,
      user: normalizeAuthUser(user.uid, user.email, userData)
    };
  } catch (error) {
    console.error('Sign in error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Sign in with Google using Redirect (more reliable)
 */
export const signInWithGoogle = async (useRedirect = true) => {
  try {
    if (isNative) {
      const { nativeGoogleSignIn } = await import('./nativeAuthService');
      return nativeGoogleSignIn();
    }

    if (useRedirect) {
      // Use redirect method - more reliable, especially on mobile
      await signInWithRedirect(auth, googleProvider);
      // After redirect, the page will reload and handleRedirectResult will be called
      return { success: true, pending: true };
    } else {
      // Use popup method - faster but can have issues
      const result = await signInWithPopup(auth, googleProvider);
      return await processGoogleUser(result.user);
    }
  } catch (error) {
    console.error('Google sign in error:', error);
    return {
      success: false,
      error: getGoogleSignInErrorMessage(error)
    };
  }
};

/**
 * Handle redirect result after Google Sign-in redirect
 */
export const handleGoogleRedirectResult = async () => {
  try {
    const result = await getRedirectResult(auth);
    if (result) {
      return await processGoogleUser(result.user);
    }
    return { success: false, noResult: true };
  } catch (error) {
    console.error('Google redirect error:', error);
    return {
      success: false,
      error: getGoogleSignInErrorMessage(error)
    };
  }
};

/**
 * Process Google user after successful authentication
 */
async function processGoogleUser(user) {
  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid));

    if (!userDoc.exists()) {
      // First time Google sign-in, create profile
      const newProfile = {
        uid: user.uid,
        email: user.email,
        name: user.displayName,
        displayName: user.displayName,
        role: 'owner', // Default role
        phone: user.phoneNumber || '',
        avatar: user.photoURL || '',
        location: '',
        pets: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'users', user.uid), newProfile);

      return {
        success: true,
        user: normalizeAuthUser(user.uid, user.email, newProfile)
      };
    }

    const data = userDoc.data();
    return {
      success: true,
      user: normalizeAuthUser(user.uid, user.email, data)
    };
  } catch (error) {
    console.error('Process Google user error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Get user-friendly error message for Google Sign-in errors
 */
function getGoogleSignInErrorMessage(error) {
  if (error.code === 'auth/network-request-failed') {
    return 'Network error. Please check your internet connection and try again. If using a VPN or ad blocker, try disabling it.';
  } else if (error.code === 'auth/popup-blocked') {
    return 'Popup was blocked. Please allow popups for this site and try again.';
  } else if (error.code === 'auth/popup-closed-by-user') {
    return 'Sign-in was cancelled. Please try again.';
  } else if (error.code === 'auth/unauthorized-domain') {
    return 'This domain is not authorized for Google Sign-in. Please add your domain to Firebase Console → Authentication → Settings → Authorized domains.';
  } else if (error.code === 'auth/cancelled-popup-request') {
    return 'Only one popup request is allowed at a time.';
  } else if (error.message && error.message.includes('INTERNAL ASSERTION FAILED')) {
    return 'Authentication error. Please refresh the page and try again.';
  }
  return error.message || 'Failed to sign in with Google';
}

/**
 * Sign out
 */
export const logout = async () => {
  try {
    if (isNative) {
      const { nativeLogout } = await import('./nativeAuthService');
      await nativeLogout();
    }
    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Sign out error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update user profile
 */
export const updateUserProfile = async (uid, updates) => {
  try {
    // Update Firestore
    await updateDoc(doc(db, 'users', uid), {
      ...updates,
      updatedAt: new Date().toISOString()
    });

    // Update Firebase Auth profile if displayName or photoURL changed
    if (updates.displayName || updates.avatar) {
      await updateProfile(auth.currentUser, {
        displayName: updates.displayName,
        photoURL: updates.avatar
      });
    }

    return { success: true };
  } catch (error) {
    console.error('Update profile error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update password
 */
export const updateUserPassword = async (newPassword) => {
  try {
    await updatePassword(auth.currentUser, newPassword);
    return { success: true };
  } catch (error) {
    console.error('Update password error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Send password reset email
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    console.error('Reset password error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Listen to auth state changes
 */
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      try {
        // Use cache first for speed
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          callback(normalizeAuthUser(user.uid, user.email, userDoc.data()));
        } else {
          callback(null);
        }
      } catch (error) {
        console.error('Auth state error:', error);
        callback(null);
      }
    } else {
      callback(null);
    }
  });
};

/**
 * Get user profile by UID
 */
export const getUserProfile = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return {
        success: true,
        user: {
          uid: userDoc.id,
          ...userDoc.data()
        }
      };
    }
    return {
      success: false,
      error: 'User not found'
    };
  } catch (error) {
    console.error('Get user profile error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

